import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { ContentItem } from "@/mocks/content";
import { typeLabels } from "@/mocks/content";

export default function HeroCarousel({ items }: { items: ContentItem[] }) {
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (items.length <= 1) return;
    const timer = setInterval(() => {
      setActive((prev) => (prev + 1) % items.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [items.length]);

  if (items.length === 0) {
    return (
      <div className="flex h-64 items-center justify-center rounded-lg bg-background-100 text-foreground-500 md:h-[420px]">
        暂无推荐内容
      </div>
    );
  }

  const current = items[active];

  return (
    <section className="relative w-full overflow-hidden rounded-lg bg-foreground-950 md:h-[440px]">
      {items.map((item, index) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            index === active ? "opacity-100" : "pointer-events-none opacity-0"
          }`}
        >
          <img
            src={item.cover}
            alt={item.title}
            title={`${item.title} - 快猫焦点推荐`}
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 via-foreground-950/40 to-foreground-950/10" />
        </div>
      ))}

      <div className="relative z-10 flex h-full min-h-[280px] w-full flex-col justify-end p-5 md:min-h-full md:p-10">
        <span className="w-fit rounded-full bg-primary-500 px-3 py-1 font-label text-xs font-semibold text-background-50">
          {typeLabels[current.type]} · 焦点推荐
        </span>
        <h2 className="mt-3 max-w-2xl font-heading text-2xl font-extrabold text-background-50 md:text-4xl">
          {current.title}
        </h2>
        <p className="mt-2 max-w-xl line-clamp-2 text-sm text-background-200 md:text-base">
          {current.summary}
        </p>
        <div className="mt-5 flex flex-wrap items-center gap-3">
          <Link
            to={`/detail/${current.id}`}
            className="flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 font-label text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-play-fill" />
            查看详情
          </Link>
          <Link
            to={`/category/${current.type}`}
            className="flex items-center gap-2 whitespace-nowrap rounded-full border border-background-200/60 px-5 py-2.5 font-label text-sm font-semibold text-background-50 transition-colors hover:bg-background-50/10"
          >
            更多{typeLabels[current.type]}
          </Link>
        </div>

        <div className="mt-6 flex items-center gap-2">
          {items.map((item, index) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setActive(index)}
              aria-label={`切换到 ${item.title}`}
              className={`h-1.5 rounded-full transition-all ${
                index === active ? "w-8 bg-primary-500" : "w-3 bg-background-50/50 hover:bg-background-50/80"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}